<script lang="ts" setup>
import { ref, watch } from 'vue';
import { useModalStore } from '@/store/modal.store';
import { useAuthStore } from '@/store/auth.store';
import { useSubscribersStore } from '@/store/subscribers.store';
import { IDevice } from '@/types';
import DefaultBtn from "@/components/block/DefaultBtn.vue";

const modalStore = useModalStore();
const authStore = useAuthStore();
const subscriberStore = useSubscribersStore();

const props = defineProps<{
	closeSignal?: boolean
}>();

watch(() => props.closeSignal, () => {
	if (props.closeSignal) {
		close();
	}
});

const device = ref<IDevice>(
	modalStore.modalPayload.device ? { ...modalStore.modalPayload.device } : {} as IDevice
);

const loading = ref<boolean>(false);

const validateIMEI = (imei: string): boolean => {
	// Basic IMEI validation - should be 15 digits
	return /^\d{15}$/.test(imei);
};

const handleSubmit = async (): Promise<void> => {
	if (!device.value.id) return;
	
	if (!device.value.deviceIMEI || !validateIMEI(device.value.deviceIMEI)) {
		alert('Please enter a valid 15-digit IMEI number');
		return;
	}

	loading.value = true;
	
	try {
		// Update device IMEI
		await subscriberStore.updateDeviceIMEI(String(device.value.id), device.value.deviceIMEI);
		
		// Update local device data
		const localDevice = authStore.subscriber.devices?.find(d => d.id === device.value.id);
		if (localDevice) {
			localDevice.deviceIMEI = device.value.deviceIMEI;
		}
		
		modalStore.closeModal();
	} catch (error) {
		console.error('Failed to update device IMEI:', error);
		alert('Failed to update device IMEI. Please try again.');
	} finally {
		loading.value = false;
	}
};

const close = (): void => {
	if (!loading.value) {
		modalStore.closeModal();
	}
};
</script>

<template>
	<div class="modal">
		<div class="modal__body">
			<div class="modal__content">
				<h3 class="modal__title">Complete Device Setup</h3>
				<p class="modal__text">
					Please enter the IMEI number for your device to complete the setup.
				</p>
				
				<form @submit.prevent="handleSubmit" class="modal__form">
					<div class="modal__input-group">
						<label for="deviceImei">Device IMEI</label>
						<input
							v-model="device.deviceIMEI"
							id="deviceImei"
							type="text"
							placeholder="Enter 15-digit IMEI number"
							class="modal__input"
							:disabled="loading"
							required
						/>
						<small class="modal__help-text">Enter the 15-digit IMEI number found on your device</small>
					</div>
					
					<div class="modal__btn">
						<DefaultBtn 
							:text="'Cancel'" 
							:type="'button'" 
							:class="'btn--white'"
							@click="close"
							:disabled="loading"
						/>
						<DefaultBtn 
							:text="'Complete Setup'" 
							:type="'submit'" 
							:class="'btn--primary'"
							:loading="loading"
						/>
					</div>
				</form>
			</div>
		</div>
	</div>
</template>

<style scoped>

.modal__content {
	padding: 32px;
}

.modal__title {
	font-size: 22px;
	font-weight: 700;
	margin-bottom: 16px;
	color: #0F172A;
	text-align: center;
}

.modal__text {
	font-size: 16px;
	line-height: 1.6;
	margin-bottom: 32px;
	color: #64748B;
	text-align: center;
}

.modal__form {
	display: flex;
	flex-direction: column;
	gap: 25px;
}

.modal__input-group {
	position: relative;
	margin-bottom: 20px;
}

.modal__input-group:last-child {
	margin-bottom: 0;
}

.modal__input-group label {
	display: block;
	color: #64748B;
	font-weight: 600;
	font-size: 14px;
	margin-bottom: 8px;
}

.modal__input {
	height: 55px;
	width: 100%;
	padding: 0 15px;
	border: 1px solid #E2E8F0;
	border-radius: 10px;
	font-size: 16px;
	transition: all 0.2s ease;
	background: #fff;
}

.modal__input:focus {
	outline: none;
	border: 1px solid #2563EB;
	box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
}

.modal__input:disabled {
	background-color: #F8FAFC;
	color: #64748B;
	border-color: #E2E8F0;
}

.modal__help-text {
	font-size: 13px;
	color: #64748B;
	margin-top: 8px;
	line-height: 1.4;
}

.modal__btn {
	display: flex;
	gap: 15px;
	margin-top: 25px;
}

.modal__btn .btn {
	min-width: 160px;
	height: 55px;
	padding: 20px;
	font-weight: 600;
	border-radius: 10px;
	border: none;
	cursor: pointer;
	transition: all 0.6s;
}

.btn--white {
	background: #fff;
	color: #0F172A;
	border: 1px solid #E2E8F0;
}

.btn--white:hover {
	background: #60A5FA;
	color: #fff;
}

.btn--white:disabled {
	background: #F8FAFC;
	color: #64748B;
	cursor: not-allowed;
}

.btn--primary {
	background: #2563EB;
	color: #fff;
}

.btn--primary:hover {
	background: #60A5FA;
}

.btn--primary:disabled {
	background: #94A3B8;
	cursor: not-allowed;
}

/* Mobile responsiveness */
@media (max-width: 640px) {
	.modal__body {
		width: 90%;
		max-width: 400px;
		margin: 20px;
	}
	
	.modal__content {
		padding: 24px;
	}
	
	.modal__title {
		font-size: 20px;
	}
	
	.modal__btn {
		flex-direction: column;
		gap: 15px;
	}
	
	.modal__btn .btn {
		width: 100%;
		min-width: auto;
	}
}
</style>
